import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import { site } from "../../data/mock";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import AdminOverview from "./AdminOverview";
import AdminAnalytics from "./AdminAnalytics";
import AdminLeads, { TEMPLATE_META, DEFAULT_TEMPLATES } from "./AdminLeads";
import AdminBlog from "./AdminBlog";
import { Anchor, LayoutDashboard, Users, PenSquare, LogOut, Loader2, Home, Bell, KeyRound, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { enablePush } from "../../lib/push";

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await api.login(email.trim().toLowerCase(), password);
      localStorage.setItem("hogtide_admin_token", res.token);
      onLogin(res.email);
    } catch (err) {
      const status = err && err.response && err.response.status;
      const msg = status === 401
        ? "Wrong email or password. Please try again."
        : "Couldn't reach the server. Please check your connection and try again.";
      setError(msg);
      toast.error("Login failed", { description: msg });
    } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a1f33] px-4">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0e2740] p-8">
        <div className="flex items-center gap-2 mb-6"><span className="rounded-lg bg-[#f5a524]/15 p-2 text-[#f5a524]"><Anchor className="h-6 w-6" /></span><div><p className="font-display text-2xl text-white leading-none">Hog Tide</p><p className="text-xs text-slate-400">Admin Dashboard</p></div></div>
        <Label className="text-slate-300">Email</Label>
        <Input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 mb-3 bg-white/5 border-white/15 text-white" placeholder="admin@hogtidefishing.com" data-testid="login-email" />
        <Label className="text-slate-300">Password</Label>
        <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 mb-3 bg-white/5 border-white/15 text-white" placeholder="••••••••" data-testid="login-password" />
        {error && <p className="mb-4 rounded-lg bg-red-500/15 border border-red-500/30 px-3 py-2 text-sm text-red-300" data-testid="login-error">{error}</p>}
        <Button type="submit" disabled={loading} className="btn-gold w-full h-11" data-testid="login-submit">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}</Button>
        <Link to="/" className="block text-center text-xs text-slate-400 hover:text-[#f5a524] mt-4">← Back to website</Link>
      </form>
    </div>
  );
};

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "leads", label: "Leads", icon: Users },
  { id: "blog", label: "AI Blogger", icon: PenSquare },
  { id: "settings", label: "Settings", icon: KeyRound },
];

const ChangePassword = () => {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (next.length < 6) { setMsg({ type: "err", text: "New password must be at least 6 characters." }); return; }
    if (next !== confirm) { setMsg({ type: "err", text: "New password and confirmation do not match." }); return; }
    setLoading(true);
    try {
      await api.changePassword(cur, next);
      setMsg({ type: "ok", text: "Password updated. Use your new password next time you log in." });
      setCur(""); setNext(""); setConfirm("");
    } catch (err) {
      const text = err?.response?.data?.detail || "Couldn't update password. Please try again.";
      setMsg({ type: "err", text });
    } finally { setLoading(false); }
  };
  return (
    <div className="max-w-md" data-testid="change-password">
      <h2 className="font-display text-3xl text-white">Change Password</h2>
      <p className="text-sm text-slate-400 mt-1 mb-6">Update the password you use to log in to this dashboard.</p>
      <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-[#0e2740] p-6 space-y-4">
        <div>
          <Label className="text-slate-300">Current password</Label>
          <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="mt-1 bg-white/5 border-white/15 text-white" data-testid="cp-current" />
        </div>
        <div>
          <Label className="text-slate-300">New password</Label>
          <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} className="mt-1 bg-white/5 border-white/15 text-white" data-testid="cp-new" />
        </div>
        <div>
          <Label className="text-slate-300">Confirm new password</Label>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 bg-white/5 border-white/15 text-white" data-testid="cp-confirm" />
        </div>
        {msg && <p className={`rounded-lg px-3 py-2 text-sm ${msg.type === "ok" ? "bg-emerald-500/15 border border-emerald-500/30 text-emerald-300" : "bg-red-500/15 border border-red-500/30 text-red-300"}`} data-testid="cp-msg">{msg.text}</p>}
        <Button type="submit" disabled={loading} className="btn-gold h-11 w-full" data-testid="cp-submit">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}</Button>
      </form>
    </div>
  );
};

const TemplatesEditor = () => {
  const [vals, setVals] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  useEffect(() => { api.getTemplates().then((r) => setVals(r.templates)).catch(() => setVals({ ...DEFAULT_TEMPLATES })); }, []);
  const setV = (id, v) => setVals((p) => ({ ...p, [id]: v }));
  const save = async () => {
    setSaving(true); setMsg(null);
    try { await api.saveTemplates(vals); setMsg({ type: "ok", text: "Templates saved — they're live for new lead replies." }); }
    catch (e) { setMsg({ type: "err", text: "Couldn't save. Please try again." }); }
    finally { setSaving(false); }
  };
  if (!vals) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#f5a524]" /></div>;
  return (
    <div className="mt-12 max-w-2xl" data-testid="templates-editor">
      <h2 className="font-display text-3xl text-white">Quick-Reply Templates</h2>
      <p className="text-sm text-slate-400 mt-1 mb-3">Edit the one-tap WhatsApp messages you send to leads. These tokens fill in automatically for each guest:</p>
      <div className="flex flex-wrap gap-2 mb-5 text-[11px]">
        {["{name}", "{trip}", "{price}", "{maps}"].map((tk) => <span key={tk} className="rounded-full bg-white/5 border border-white/10 px-2.5 py-1 text-slate-300 font-mono">{tk}</span>)}
      </div>
      <div className="space-y-5">
        {TEMPLATE_META.map((m) => (
          <div key={m.id} className="rounded-2xl border border-white/10 bg-[#0e2740] p-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-white font-heading flex items-center gap-2"><m.icon className="h-4 w-4 text-[#25D366]" /> {m.label}</Label>
              <button type="button" onClick={() => setV(m.id, DEFAULT_TEMPLATES[m.id])} className="text-[11px] text-slate-400 hover:text-[#f5a524]" data-testid={`tpl-reset-${m.id}`}>Reset to default</button>
            </div>
            <Textarea value={vals[m.id] || ""} onChange={(e) => setV(m.id, e.target.value)} rows={3} className="bg-white/5 border-white/15 text-white text-sm" data-testid={`tpl-input-${m.id}`} />
          </div>
        ))}
      </div>
      {msg && <p className={`mt-4 rounded-lg px-3 py-2 text-sm ${msg.type === "ok" ? "bg-emerald-500/15 border border-emerald-500/30 text-emerald-300" : "bg-red-500/15 border border-red-500/30 text-red-300"}`} data-testid="tpl-msg">{msg.text}</p>}
      <Button onClick={save} disabled={saving} className="btn-gold h-11 mt-5 px-8" data-testid="tpl-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save templates"}</Button>
    </div>
  );
};

const Admin = () => {
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("overview");
  const [newLeads, setNewLeads] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const focusLead = searchParams.get("lead");

  useEffect(() => {
    if (focusLead) setTab("leads");
  }, [focusLead]);

  useEffect(() => {
    const token = localStorage.getItem("hogtide_admin_token");
    if (!token) { setAuthed(false); return; }
    api.me().then(() => setAuthed(true)).catch(() => { localStorage.removeItem("hogtide_admin_token"); setAuthed(false); });
  }, []);

  useEffect(() => {
    if (authed) {
      const load = () => api.analytics().then((d) => setNewLeads(d.new_leads)).catch(() => {});
      load();
      const t = setInterval(load, 20000);
      return () => clearInterval(t);
    }
  }, [authed]);

  const logout = () => { localStorage.removeItem("hogtide_admin_token"); setAuthed(false); };

  const enableAlerts = async () => {
    try {
      await enablePush();
      toast.success("Push notifications enabled", { description: "You'll get a popup for every new lead — even when this tab is closed." });
    } catch (e) {
      toast.error("Couldn't enable notifications", { description: e.message });
    }
  };

  if (authed === null) return <div className="min-h-screen bg-[#0a1f33] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#f5a524]" /></div>;
  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-[#0a1f33] text-slate-100 flex">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r border-white/10 bg-[#071726] p-4 hidden md:flex flex-col">
        <div className="flex items-center gap-2 mb-8 px-2"><span className="rounded-lg bg-[#f5a524]/15 p-2 text-[#f5a524]"><Anchor className="h-5 w-5" /></span><div><p className="font-display text-xl leading-none">Hog Tide</p><p className="text-[10px] text-slate-400">Admin</p></div></div>
        <nav className="flex flex-col gap-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition ${tab === t.id ? "bg-[#f5a524] text-[#0a1f33] font-semibold" : "text-slate-300 hover:bg-white/5"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
              {t.id === "leads" && newLeads > 0 && <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] ${tab===t.id?"bg-[#0a1f33] text-white":"bg-[#f5a524] text-[#0a1f33]"}`}>{newLeads}</span>}
            </button>
          ))}
        </nav>
        <div className="mt-auto flex flex-col gap-1">
          <button onClick={enableAlerts} data-testid="enable-alerts-btn" className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-[#f5a524] hover:bg-white/5"><Bell className="h-4 w-4" /> Enable alerts</button>
          <Link to="/" className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-300 hover:bg-white/5"><Home className="h-4 w-4" /> View site</Link>
          <button onClick={logout} className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-300 hover:bg-white/5"><LogOut className="h-4 w-4" /> Logout</button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile tabs */}
        <div className="md:hidden flex gap-1 border-b border-white/10 bg-[#071726] p-2 overflow-x-auto">
          {TABS.map((t) => (<button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs whitespace-nowrap ${tab===t.id?"bg-[#f5a524] text-[#0a1f33] font-semibold":"text-slate-300"}`}><t.icon className="h-3.5 w-3.5" />{t.label}{t.id==="leads"&&newLeads>0&&<span className="rounded-full bg-red-500 px-1.5 text-white">{newLeads}</span>}</button>))}
          <button onClick={enableAlerts} className="ml-auto text-[#f5a524] px-2" title="Enable alerts"><Bell className="h-4 w-4" /></button>
          <button onClick={logout} className="text-slate-400 px-2"><LogOut className="h-4 w-4" /></button>
        </div>
        <main className="p-5 md:p-8 overflow-y-auto">
          {tab === "overview" && <AdminOverview onGoLeads={() => setTab("leads")} />}
          {tab === "analytics" && <AdminAnalytics />}
          {tab === "leads" && <AdminLeads focusLeadId={focusLead} onFocusHandled={() => setSearchParams({}, { replace: true })} onChange={() => api.analytics().then((d) => setNewLeads(d.new_leads)).catch(()=>{})} />}
          {tab === "blog" && <AdminBlog />}
          {tab === "settings" && <><ChangePassword /><TemplatesEditor /></>}
        </main>
      </div>
    </div>
  );
};

export default Admin;
